import { ArrowLeft, FileCode2, PencilRuler, TriangleAlert, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  openFrameLabArchive,
  type FrameLabArchiveView,
} from './frameLabDocument'
import { ProjectCanvas } from './ProjectCanvas'

const fixtureURL = '/fixtures/minimal-frame.framelab'

type ViewerState =
  | { status: 'loading' }
  | { status: 'ready'; archive: FrameLabArchiveView }
  | { status: 'error'; message: string }

export function ProjectViewer({ onOpenBase, onOpenGallery, initialFile }: { onOpenBase?: () => void; onOpenGallery?: () => void; initialFile?: File }) {
  const [state, setState] = useState<ViewerState>({ status: 'loading' })
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (initialFile) void openSelectedFile(initialFile)
    else void openFixture()
  }, [initialFile])

  async function openFixture() {
    setState({ status: 'loading' })
    try {
      const response = await fetch(fixtureURL, { cache: 'no-store' })
      if (!response.ok) {
        throw new Error(`Fixture non disponibile: HTTP ${response.status}`)
      }
      const archive = await openFrameLabArchive('minimal-frame.framelab', new Uint8Array(await response.arrayBuffer()))
      setState({ status: 'ready', archive })
    } catch (error) {
      setState({ status: 'error', message: messageFrom(error) })
    }
  }

  async function openSelectedFile(file: File | undefined) {
    if (!file) {
      return
    }
    setState({ status: 'loading' })
    try {
      const archive = await openFrameLabArchive(file.name, new Uint8Array(await file.arrayBuffer()))
      setState({ status: 'ready', archive })
    } catch (error) {
      setState({ status: 'error', message: messageFrom(error) })
    } finally {
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  return (
    <main className="viewer-shell">
      <header className="viewer-toolbar">
        <div>
          <p className="eyebrow">ForceCAD Free</p>
          <h1>Visualizzatore progetto</h1>
        </div>
        <div className="toolbar-actions">
          <button type="button" className="ghost-button" onClick={onOpenGallery} aria-label="Torna ai progetti">
            <ArrowLeft aria-hidden="true" />
            <span>Progetti</span>
          </button>
          <button type="button" className="ghost-button" onClick={() => void openFixture()}>
            <FileCode2 aria-hidden="true" />
            <span>Fixture</span>
          </button>
          <button type="button" className="ghost-button" onClick={onOpenBase}>
            <PencilRuler aria-hidden="true" />
            <span>Editor Base</span>
          </button>
          <button type="button" className="primary-button" onClick={() => inputRef.current?.click()}>
            <Upload aria-hidden="true" />
            <span>Apri .framelab</span>
          </button>
          <input
            ref={inputRef}
            className="file-input"
            type="file"
            accept=".framelab,application/zip"
            onChange={(event) => void openSelectedFile(event.currentTarget.files?.[0])}
          />
        </div>
      </header>

      {state.status === 'loading' && <div className="viewer-empty">Caricamento progetto</div>}
      {state.status === 'error' && (
        <div className="viewer-error">
          <TriangleAlert aria-hidden="true" />
          <span>{state.message}</span>
        </div>
      )}
      {state.status === 'ready' && <ArchiveView archive={state.archive} />}
    </main>
  )
}

function ArchiveView({ archive }: { archive: FrameLabArchiveView }) {
  return (
    <section className="viewer-layout">
      <ProjectCanvas archive={archive} />
      <aside className="inspector">
        <div>
          <p className="inspector-label">File</p>
          <h2>{archive.project.title}</h2>
          <p className="file-name">{archive.fileName}</p>
        </div>
        <dl className="viewer-metrics">
          <div>
            <dt>Nodi</dt>
            <dd>{archive.project.nodes.length}</dd>
          </div>
          <div>
            <dt>Elementi</dt>
            <dd>{archive.project.elements.length}</dd>
          </div>
          <div>
            <dt>Vincoli</dt>
            <dd>{archive.project.supports.length}</dd>
          </div>
          <div>
            <dt>Carichi</dt>
            <dd>{archive.project.loads.length}</dd>
          </div>
        </dl>
        {archive.unsupportedCapabilities.length > 0 && (
          <div className="capability-warning">
            <TriangleAlert aria-hidden="true" />
            <p>Il file richiede capability non rappresentate: {archive.unsupportedCapabilities.join(', ')}</p>
          </div>
        )}
        <p className="capability-list">{(archive.project.requiredCapabilities ?? archive.manifest.requiredCapabilities).join(', ')}</p>
      </aside>
    </section>
  )
}

function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}
